import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  pricingRaiseWriteResult,
  pricingGoalApplyResult,
  pricingGoalUndoResult,
  pricingBulkApplyResult,
  pricingOperationUndoResult,
  type PricingBulkIntent,
  type PricingBulkSelection,
  type PricingBulkPreviewResult,
  type PricingBulkApplyInput,
  type PricingOperationUndoInput,
  type PricingOperationUndoPreviewResult,
  type PricingGoalSelection,
  type PricingGoalPreviewResult,
  type PricingGoalApplyInput,
  type PricingGoalUndoInput,
  type PricingGoalUndoPreviewResult,
  type PricingRaiseKeepInput,
  type PricingRaiseUndoInput,
  type PricingRaiseUndoPreviewResult,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import { isStockBusy, refusalReason, refusalStatus } from '../../lib/refusal';
import { useSession } from '../session';
import { useOnline } from '../ui/useConnection';
import { usePendingWriteNavigation } from './usePoReceiptWrite';
type Phase =
  | 'previewing'
  | 'preview'
  | 'preview_failed'
  | 'writing'
  | 'retry'
  | 'stale'
  | 'reconcile'
  | 'refreshing'
  | 'read_failed';
type SavedCounts = { selectedCount: number; changedCount: number; skippedCount: number };
type BaseEntry = {
  phase: Phase;
  note: string;
  submitted: boolean;
  accepted: boolean;
  uncertain: boolean;
  running: boolean;
  hidden: boolean;
  acceptedOperationId?: string | null;
  acceptedCounts?: SavedCounts;
  expectedChangedCount?: number;
  error?: string;
  opener?: HTMLElement;
  openerLabel?: string;
  surface?: HTMLElement;
};
type AutoEntry = BaseEntry & {
  kind: 'auto';
  action: 'keep' | 'undo';
  raiseMemberId: string;
  preview?: PricingRaiseUndoPreviewResult;
  input?: PricingRaiseKeepInput | PricingRaiseUndoInput;
};
type GoalApplyEntry = BaseEntry & {
  kind: 'goal';
  action: 'goal_apply';
  terminalRejected?: boolean;
  chooseScope: boolean;
  scopeKind: 'category' | 'vendor';
  selection?: PricingGoalSelection;
  selectionLabel?: string;
  preview?: PricingGoalPreviewResult;
  input?: PricingGoalApplyInput;
  pageOffset: number;
  requestedOffset?: number;
  previousIds?: readonly string[];
  membership?: { added: number; removed: number };
};
type GoalUndoEntry = BaseEntry & {
  kind: 'goal';
  action: 'goal_undo';
  terminalRejected?: boolean;
  goalMemberId: string;
  preview?: PricingGoalUndoPreviewResult;
  input?: PricingGoalUndoInput;
};
type BulkEntry = BaseEntry & {
  kind: 'bulk';
  action: 'bulk_apply';
  terminalRejected?: boolean;
  scopeKind: 'category' | 'vendor';
  selection?: PricingBulkSelection;
  selectionLabel?: string;
  direction: 'raise' | 'lower';
  percentageText: string;
  intent?: PricingBulkIntent;
  preview?: PricingBulkPreviewResult;
  input?: PricingBulkApplyInput;
  pageOffset: number;
  requestedOffset?: number;
  previousIds?: readonly string[];
  membership?: { added: number; removed: number };
};
type OperationUndoEntry = BaseEntry & {
  kind: 'operation_undo';
  action: 'operation_undo';
  terminalRejected?: boolean;
  operationId: string;
  preview?: PricingOperationUndoPreviewResult;
  input?: PricingOperationUndoInput;
  pageOffset: number;
  requestedOffset?: number;
  previousIds?: readonly string[];
  previousEligibleIds?: readonly string[];
  membership?: { added: number; removed: number };
  eligibility?: { added: number; removed: number };
};
type Entry = AutoEntry | GoalApplyEntry | GoalUndoEntry | BulkEntry | OperationUndoEntry;
// This only parses user intent. All price, rounding and eligibility decisions are server-owned.
function percentageBps(raw: string): number | undefined {
  const match = /^(\d{1,2})(?:\.(\d{1,2}))?$/.exec(raw);
  if (!match) return;
  const bps = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return bps >= 10 && bps <= 5000 ? bps : undefined;
}
function cohortDifference(before: readonly string[], after: readonly string[]) {
  const old = new Set(before),
    current = new Set(after);
  return {
    added: after.filter((id) => !old.has(id)).length,
    removed: before.filter((id) => !current.has(id)).length,
  };
}
function equalIds(before: readonly string[], after: readonly string[]) {
  return before.length === after.length && before.every((id, i) => id === after[i]);
}
type Completion = {
  owner: string;
  operationId?: string | null;
  counts?: SavedCounts;
  kind: Entry['kind'];
  action: Entry['action'] | 'cancel';
  memberId: string;
  seq: number;
  fallback?: boolean;
  opener?: HTMLElement;
  openerLabel?: string;
  surface?: HTMLElement;
};
function historySurface(opener?: HTMLElement): HTMLElement | undefined {
  let node: Node | null = opener ?? null;
  while (node) {
    if (
      'getAttribute' in node &&
      (node as Element).getAttribute('data-pricing-history-surface') === ''
    )
      return node as HTMLElement;
    node = node.parentNode;
  }
}
function focusable(node: HTMLElement | null | undefined): HTMLElement | null {
  return node &&
    document.contains(node) &&
    !node.hasAttribute('disabled') &&
    !node.closest?.('[aria-hidden="true"],[inert]')
    ? node
    : null;
}
/** The Inventory page owns this session-only record above local tabs and all Sheets. */
export function usePricingReviewWrite() {
  const session = useSession(),
    owner = `${session.orgId}:${session.userId}:${session.role}`;
  const current = useRef(owner),
    generation = useRef(0),
    alive = useRef(true),
    records = useRef(new Map<string, Entry>()),
    keys = useRef(createIntentKey());
  const [version, tick] = useState(0),
    [completion, setCompletion] = useState<Completion>(),
    sequence = useRef(0),
    focusHandled = useRef(0);
  if (current.current !== owner) {
    current.current = owner;
    generation.current++;
  }
  const qc = useQueryClient(),
    trpc = useTRPC(),
    online = useOnline(),
    allowed = session.role === 'owner' || session.role === 'office';
  const keep = useMutation(
    trpc.pricing.raiseKeep.mutationOptions({ networkMode: 'always', retry: false }),
  );
  const undo = useMutation(
    trpc.pricing.raiseUndo.mutationOptions({ networkMode: 'always', retry: false }),
  );
  const goalApply = useMutation(
    trpc.pricing.goalApply.mutationOptions({ networkMode: 'always', retry: false }),
  );
  const goalUndo = useMutation(
    trpc.pricing.goalUndo.mutationOptions({ networkMode: 'always', retry: false }),
  );
  const bulkApply = useMutation(
    trpc.pricing.bulkApply.mutationOptions({ networkMode: 'always', retry: false }),
  );
  const operationUndo = useMutation(
    trpc.pricing.operationUndo.mutationOptions({ networkMode: 'always', retry: false }),
  );
  const active = (gen = generation.current) =>
    alive.current && current.current === owner && generation.current === gen;
  const notify = () => {
    if (active()) tick((n) => n + 1);
  };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const hasPending = useCallback(
    () => [...records.current.values()].some((a) => a.submitted),
    [],
  );
  const shouldBlock = useCallback(
    () =>
      hasPending() &&
      !window.confirm(
        [...records.current.values()].some((a) => a.kind === 'goal' && a.submitted)
          ? 'This goal price change is unresolved. Leaving ends this session-only retry. Leave anyway?'
          : 'This price review is unresolved. Leaving ends this session-only retry. Leave anyway?',
      ),
    [hasPending],
  );
  usePendingWriteNavigation(hasPending, shouldBlock);
  const entry = allowed ? records.current.get(owner) : undefined;
  const attempt = entry?.kind === 'auto' ? entry : undefined;
  const goalAttempt = entry?.kind === 'goal' ? entry : undefined;
  const bulkAttempt = entry?.kind === 'bulk' ? entry : undefined;
  const operationUndoAttempt = entry?.kind === 'operation_undo' ? entry : undefined;
  useEffect(() => {
    if (
      !completion ||
      completion.owner !== owner ||
      focusHandled.current === completion.seq ||
      !allowed
    )
      return;
    let frame = 0,
      gone = false,
      focused: HTMLElement | null = null;
    const restore = () => {
      if (!active()) return;
      if (
        document.querySelector(
          completion.kind === 'bulk'
            ? '[data-pricing-bulk-sheet=""]'
            : completion.kind === 'operation_undo'
              ? '[data-pricing-operation-undo-sheet=""]'
              : completion.kind === 'goal'
                ? '[data-pricing-goal-sheet=""]'
                : '[data-pricing-review-sheet=""]',
        )
      ) {
        gone = false;
        focused = null;
      } else if (gone) {
        const surface = focusable(completion.surface);
        const member = surface?.querySelector(
          `[data-price-member="${completion.memberId}"]`,
        );
        const stable = member?.querySelector(
          '[data-price-review-action=""]',
        ) as HTMLElement | null;
        const sameOpener =
          completion.kind === 'auto' ||
          !completion.openerLabel ||
          (completion.opener?.getAttribute('aria-label') ??
            completion.opener?.textContent) === completion.openerLabel;
        const original = sameOpener ? focusable(completion.opener) : null;
        const markers = Array.from(
          (surface ?? document).querySelectorAll?.('[data-pricing-history-focus=""]') ??
            [],
        ).filter((node) => focusable(node as HTMLElement));
        const fallback = (
          surface?.hasAttribute('data-pricing-history-focus') ? surface : markers.at(-1)
        ) as HTMLElement | undefined;
        const toolbar = focusable(
          document.querySelector<HTMLElement>(
            completion.kind === 'bulk'
              ? '[data-pricing-bulk-opener=""]'
              : completion.kind === 'goal'
                ? '[data-pricing-goal-opener=""]'
                : '[data-pricing-history-opener=""]',
          ),
        );

        const target =
          completion.kind === 'goal' || completion.kind === 'bulk'
            ? (original ?? focusable(stable) ?? toolbar)
            : (original ?? focusable(stable) ?? fallback ?? toolbar);
        // Sheet close autofocus is deferred. Confirm ownership on a later frame,
        // so its cleanup cannot steal focus after this completion is marked handled.
        if (
          (target && focused === target && document.activeElement === target) ||
          (!target && completion.kind === 'auto')
        ) {
          focusHandled.current = completion.seq;
          return;
        }
        target?.focus();
        focused = target ?? null;
        if (
          target &&
          document.activeElement === target &&
          !completion.fallback &&
          (completion.kind === 'goal' || completion.kind === 'bulk'
            ? target === toolbar
            : !original && !stable)
        )
          setCompletion({ ...completion, fallback: true });
      } else gone = true;
      frame = requestAnimationFrame(restore);
    };
    frame = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frame);
  }, [completion, owner, allowed]);
  const finish = (a: Entry, action: Completion['action']) => {
    records.current.delete(owner);
    keys.current.clear();
    setCompletion({
      owner,
      action,
      kind: a.kind,
      operationId: a.acceptedOperationId,
      counts: a.acceptedCounts,
      memberId:
        a.kind === 'auto'
          ? a.raiseMemberId
          : a.action === 'goal_undo'
            ? a.goalMemberId
            : a.action === 'operation_undo'
              ? a.operationId
              : '',
      seq: ++sequence.current,
      opener: a.opener,
      openerLabel: a.openerLabel,
      surface: a.surface,
    });
    notify();
  };
  const withholdPrivateReads = async (error: unknown, gen: number) => {
    if (![401, 403].includes(refusalStatus(error)) || !active(gen)) return;
    const a = records.current.get(owner);
    if (a) {
      a.hidden = true;
      a.preview = undefined;
    }
    notify();
    // A current authority refusal invalidates every protected pricing projection,
    // including the nav count. Keep the frozen write record for exact reconciliation.
    await qc.cancelQueries({ queryKey: trpc.pricing.pathKey() }).catch(() => {});
    if (active(gen)) qc.removeQueries({ queryKey: trpc.pricing.pathKey() });
  };
  const refresh = async () => {
    const a = records.current.get(owner);
    if (!a || !a.accepted || a.running || !active() || !allowed) return;
    a.running = true;
    a.phase = 'refreshing';
    a.error = undefined;
    notify();
    const gen = generation.current;
    try {
      await Promise.all([
        qc.invalidateQueries(
          { queryKey: trpc.pricing.pathKey() },
          { throwOnError: true },
        ),
        qc.invalidateQueries(
          { queryKey: trpc.priceBook.pathKey() },
          { throwOnError: true },
        ),
      ]);
      if (!active(gen)) {
        a.phase = 'read_failed';
        return;
      }
      if (a.kind !== 'auto') {
        // Current protected reads and committed operation evidence reauthorize recovery.
        // A new proposal may be unavailable after commit and is not completion evidence.
        await Promise.all([
          qc.fetchQuery({
            ...trpc.pricing.current.queryOptions({ limit: 25, offset: 0 }),
            staleTime: 0,
            retry: false,
          }),
          qc.fetchQuery({
            ...trpc.pricing.raisedCount.queryOptions(),
            staleTime: 0,
            retry: false,
          }),
          qc.fetchQuery({
            ...trpc.priceBook.list.queryOptions(),
            staleTime: 0,
            retry: false,
          }),
          ...(a.acceptedOperationId
            ? [
                qc.fetchQuery({
                  ...trpc.pricing.history.queryOptions({
                    operationId: a.acceptedOperationId,
                  }),
                  staleTime: 0,
                  retry: false,
                }),
                qc.fetchQuery({
                  ...trpc.pricing.historyMembers.queryOptions({
                    operationId: a.acceptedOperationId,
                  }),
                  staleTime: 0,
                  retry: false,
                }),
              ]
            : []),
        ]);
        if (!active(gen)) {
          a.phase = 'read_failed';
          return;
        }
      }
      if (!active(gen)) {
        a.phase = 'read_failed';
        return;
      }
      finish(a, a.action);
    } catch (error) {
      a.phase = 'read_failed';
      a.error = refusalReason(error);
      if (refusalStatus(error) === 403 || refusalStatus(error) === 401) a.hidden = true;
      await withholdPrivateReads(error, gen);
    } finally {
      a.running = false;
      notify();
    }
  };
  const send = async () => {
    const a = records.current.get(owner);
    if (!a?.input || a.accepted || a.running || !active() || !allowed || !online) return;
    a.running = true;
    a.phase = 'writing';
    a.error = undefined;
    notify();
    const gen = generation.current;
    try {
      if (a.action === 'bulk_apply') {
        const reply = pricingBulkApplyResult.parse(
          await bulkApply.mutateAsync(a.input as PricingBulkApplyInput),
        );
        a.acceptedOperationId = reply.operationId;
        if (
          reply.revision !== a.input.expectedRevision ||
          reply.selectedCount !== a.input.reviewedItemIds.length ||
          reply.changedCount !== a.expectedChangedCount
        )
          throw new Error('The saved bulk change needs reconciliation.');
        a.acceptedCounts = {
          selectedCount: reply.selectedCount,
          changedCount: reply.changedCount,
          skippedCount: reply.skippedCount,
        };
      } else if (a.action === 'operation_undo') {
        const reply = pricingOperationUndoResult.parse(
          await operationUndo.mutateAsync(a.input as PricingOperationUndoInput),
        );
        a.acceptedOperationId = reply.undoOperationId;
        if (
          reply.sourceOperationId !== a.operationId ||
          reply.revision !== a.input.expectedRevision ||
          reply.selectedCount !== a.input.reviewedMemberIds.length ||
          reply.changedCount !== a.expectedChangedCount
        )
          throw new Error('The saved operation Undo needs reconciliation.');
        a.acceptedCounts = {
          selectedCount: reply.selectedCount,
          changedCount: reply.changedCount,
          skippedCount: reply.skippedCount,
        };
      } else if (a.action === 'goal_apply') {
        const reply = pricingGoalApplyResult.parse(
          await goalApply.mutateAsync(a.input as PricingGoalApplyInput),
        );
        a.acceptedOperationId = reply.operationId;
        if (
          reply.revision !== a.input.expectedRevision ||
          reply.selectedCount !== a.input.reviewedItemIds.length
        )
          throw new Error('The saved goal price change needs reconciliation.');
      } else if (a.action === 'goal_undo') {
        const reply = pricingGoalUndoResult.parse(
          await goalUndo.mutateAsync(a.input as PricingGoalUndoInput),
        );
        a.acceptedOperationId = reply.undoOperationId;
        if (reply.goalMemberId !== a.goalMemberId)
          throw new Error('The saved goal Undo needs reconciliation.');
      } else {
        const raw =
          a.action === 'keep'
            ? await keep.mutateAsync(a.input as PricingRaiseKeepInput)
            : await undo.mutateAsync(a.input as PricingRaiseUndoInput);
        const reply = pricingRaiseWriteResult.parse(raw);
        if (reply.action !== a.action || reply.raiseMemberId !== a.raiseMemberId)
          throw new Error('The saved price review needs reconciliation.');
      }
      a.accepted = true;
      a.hidden = false;
      a.phase = 'read_failed';
    } catch (error) {
      const status = refusalStatus(error),
        data = (error as { data?: { code?: string } })?.data;
      if (status === 0 || status === 408 || status >= 500) a.uncertain = true;
      if (status === 401 || status === 403) a.hidden = true;
      if (
        (a.action === 'undo' || a.kind !== 'auto') &&
        status === 412 &&
        data?.code === 'PRECONDITION_FAILED'
      ) {
        a.phase = 'stale';
        a.uncertain = false;
        if (a.kind !== 'auto') a.terminalRejected = true;
      } else if (status === 409 && !isStockBusy(error)) a.phase = 'reconcile';
      else a.phase = 'retry';
      a.error = refusalReason(error);
      await withholdPrivateReads(error, gen);
      return;
    } finally {
      a.running = false;
      notify();
    }
    if (active(gen)) await refresh();
  };
  const loadPreview = async (a: Entry, offset = 0, paging = false) => {
    if (a.running || !active() || !online) return;
    if ((a.action === 'goal_apply' || a.action === 'bulk_apply') && !paging && a.preview)
      a.previousIds = a.preview.reviewedItemIds;
    if (a.action === 'operation_undo' && !paging && a.preview) {
      a.previousIds = a.preview.reviewedMemberIds;
      a.previousEligibleIds = a.preview.eligibleMemberIds;
    }
    if (a.kind === 'bulk' || a.kind === 'operation_undo') a.requestedOffset = offset;
    a.running = true;
    a.phase = 'previewing';
    a.error = undefined;
    notify();
    const gen = generation.current;
    try {
      if (a.action === 'bulk_apply') {
        if (!a.intent) return;
        const preview = await qc.fetchQuery({
          ...trpc.pricing.bulkPreview.queryOptions({
            ...a.intent,
            offset,
            ...(paging && a.preview ? { expectedRevision: a.preview.revision } : {}),
          }),
          staleTime: 0,
          retry: false,
        });
        if (!active(gen)) {
          a.phase = 'preview_failed';
          return;
        }
        if (
          JSON.stringify(preview.selection) !== JSON.stringify(a.intent.selection) ||
          preview.direction !== a.intent.direction ||
          preview.magnitudeBps !== a.intent.magnitudeBps ||
          (paging &&
            a.preview &&
            (preview.revision !== a.preview.revision ||
              !equalIds(preview.reviewedItemIds, a.preview.reviewedItemIds)))
        )
          throw new Error('This preview changed. Review current prices before applying.');
        if (!paging && a.previousIds)
          a.membership = cohortDifference(a.previousIds, preview.reviewedItemIds);
        a.preview = preview;
        a.pageOffset = offset;
      } else if (a.action === 'operation_undo') {
        const preview = await qc.fetchQuery({
          ...trpc.pricing.operationUndoPreview.queryOptions({
            operationId: a.operationId,
            offset,
            ...(paging && a.preview ? { expectedRevision: a.preview.revision } : {}),
          }),
          staleTime: 0,
          retry: false,
        });
        if (!active(gen)) {
          a.phase = 'preview_failed';
          return;
        }
        if (
          preview.operationId !== a.operationId ||
          (paging &&
            a.preview &&
            (preview.revision !== a.preview.revision ||
              !equalIds(preview.reviewedMemberIds, a.preview.reviewedMemberIds) ||
              !equalIds(preview.eligibleMemberIds, a.preview.eligibleMemberIds)))
        )
          throw new Error(
            'This Undo preview changed. Review current prices before applying.',
          );
        if (!paging && a.previousIds)
          a.membership = cohortDifference(a.previousIds, preview.reviewedMemberIds);
        if (!paging && a.previousEligibleIds)
          a.eligibility = cohortDifference(
            a.previousEligibleIds,
            preview.eligibleMemberIds,
          );
        a.preview = preview;
        a.pageOffset = offset;
      } else if (a.action === 'goal_apply') {
        if (!a.selection) return;
        const preview = await qc.fetchQuery({
          ...trpc.pricing.goalPreview.queryOptions({
            selection: a.selection,
            offset,
            ...(paging && a.preview ? { expectedRevision: a.preview.revision } : {}),
          }),
          staleTime: 0,
          retry: false,
        });
        if (!active(gen)) {
          a.phase = 'preview_failed';
          return;
        }
        if (
          paging &&
          a.preview &&
          (preview.revision !== a.preview.revision ||
            JSON.stringify(preview.reviewedItemIds) !==
              JSON.stringify(a.preview.reviewedItemIds))
        )
          throw new Error('This preview changed. Review current prices before applying.');
        if (!paging && a.previousIds) {
          const before = new Set(a.previousIds),
            after = new Set(preview.reviewedItemIds);
          a.membership = {
            added: preview.reviewedItemIds.filter((id) => !before.has(id)).length,
            removed: a.previousIds.filter((id) => !after.has(id)).length,
          };
        }
        a.preview = preview;
        a.pageOffset = offset;
      } else if (a.action === 'goal_undo') {
        const preview = await qc.fetchQuery({
          ...trpc.pricing.goalUndoPreview.queryOptions({ goalMemberId: a.goalMemberId }),
          staleTime: 0,
          retry: false,
        });
        if (!active(gen)) {
          a.phase = 'preview_failed';
          return;
        }
        a.preview = preview;
      } else {
        const preview = await qc.fetchQuery({
          ...trpc.pricing.raiseUndoPreview.queryOptions({
            raiseMemberId: a.raiseMemberId,
          }),
          staleTime: 0,
          retry: false,
        });
        if (!active(gen)) {
          a.phase = 'preview_failed';
          return;
        }
        a.preview = preview;
      }
      a.hidden = false;
      a.phase = 'preview';
      a.submitted = false;
      a.uncertain = false;
      if (a.kind !== 'auto') a.terminalRejected = false;
      a.input = undefined;
      keys.current.clear();
    } catch (error) {
      a.phase =
        a.submitted || paging || (a.kind !== 'auto' && refusalStatus(error) === 412)
          ? 'stale'
          : 'preview_failed';
      a.error = refusalReason(error);
      if (refusalStatus(error) === 403 || refusalStatus(error) === 401) a.hidden = true;
      await withholdPrivateReads(error, gen);
    } finally {
      a.running = false;
      notify();
    }
  };
  const openUndo = (raiseMemberId: string, opener?: HTMLElement) => {
    if (records.current.has(owner) || !active() || !allowed || !online) return;
    const a: AutoEntry = {
      kind: 'auto',
      action: 'undo',
      raiseMemberId,
      phase: 'previewing',
      note: '',
      submitted: false,
      accepted: false,
      uncertain: false,
      running: false,
      hidden: false,
      opener,
      surface: historySurface(opener),
    };
    records.current.set(owner, a);
    setCompletion(undefined);
    notify();
    void loadPreview(a);
  };
  const beginKeep = (raiseMemberId: string, opener?: HTMLElement) => {
    if (records.current.has(owner) || !active() || !allowed || !online) return;
    const input = Object.freeze({
      raiseMemberId,
      ...keys.current.forIntent([owner, 'keep', raiseMemberId]),
    });
    records.current.set(owner, {
      kind: 'auto',
      action: 'keep',
      raiseMemberId,
      phase: 'writing',
      note: '',
      input,
      submitted: true,
      accepted: false,
      uncertain: false,
      running: false,
      hidden: false,
      opener,
      surface: historySurface(opener),
    });
    setCompletion(undefined);
    notify();
    void send();
  };
  const confirmUndo = () => {
    const a = records.current.get(owner);
    if (
      !a ||
      a.action !== 'undo' ||
      a.submitted ||
      a.running ||
      a.phase !== 'preview' ||
      a.preview?.status !== 'eligible' ||
      a.hidden ||
      !active() ||
      !online
    )
      return;
    const intent = {
      raiseMemberId: a.raiseMemberId,
      expectedRevision: a.preview.revision,
      note: a.note || null,
    };
    a.input = Object.freeze({
      ...intent,
      ...keys.current.forIntent([owner, 'undo', intent]),
    });
    a.submitted = true;
    notify();
    void send();
  };
  const openGoal = (selection?: PricingGoalSelection, opener?: HTMLElement) => {
    if (records.current.has(owner) || !active() || !allowed || !online) return;
    const a: GoalApplyEntry = {
      kind: 'goal',
      action: 'goal_apply',
      selection,
      chooseScope: !selection,
      scopeKind: selection?.kind === 'vendor' ? 'vendor' : 'category',
      phase: selection ? 'previewing' : 'preview',
      pageOffset: 0,
      note: '',
      submitted: false,
      accepted: false,
      uncertain: false,
      running: false,
      hidden: false,
      opener,
      openerLabel: opener?.getAttribute('aria-label') ?? opener?.textContent ?? undefined,
      surface: historySurface(opener),
    };
    records.current.set(owner, a);
    setCompletion(undefined);
    notify();
    if (selection) void loadPreview(a);
  };
  const openGoalUndo = (goalMemberId: string, opener?: HTMLElement) => {
    if (records.current.has(owner) || !active() || !allowed || !online) return;
    const a: GoalUndoEntry = {
      kind: 'goal',
      action: 'goal_undo',
      goalMemberId,
      phase: 'previewing',
      note: '',
      submitted: false,
      accepted: false,
      uncertain: false,
      running: false,
      hidden: false,
      opener,
      openerLabel: opener?.getAttribute('aria-label') ?? opener?.textContent ?? undefined,
      surface: historySurface(opener),
    };
    records.current.set(owner, a);
    setCompletion(undefined);
    notify();
    void loadPreview(a);
  };
  const confirmGoal = () => {
    const a = records.current.get(owner);
    if (
      !a ||
      a.kind !== 'goal' ||
      a.submitted ||
      a.running ||
      a.hidden ||
      a.phase !== 'preview' ||
      !active() ||
      !online ||
      !allowed
    )
      return;
    if (a.action === 'goal_apply') {
      if (
        !a.selection ||
        !a.preview ||
        a.preview.summary.changedCount === 0 ||
        a.preview.summary.blockingCount > 0
      )
        return;
      const intent = {
        selection: Object.freeze({ ...a.selection }),
        reviewedItemIds: Object.freeze([
          ...a.preview.reviewedItemIds,
        ]) as unknown as string[],
        expectedRevision: a.preview.revision,
      };
      a.previousIds = intent.reviewedItemIds;
      a.input = Object.freeze({
        ...intent,
        ...keys.current.forIntent([owner, 'goal_apply', intent]),
      });
    } else {
      if (a.preview?.status !== 'eligible') return;
      const intent = {
        goalMemberId: a.goalMemberId,
        expectedRevision: a.preview.revision,
        note: a.note || null,
      };
      a.input = Object.freeze({
        ...intent,
        ...keys.current.forIntent([owner, 'goal_undo', intent]),
      });
    }
    a.submitted = true;
    notify();
    void send();
  };
  const openBulk = (opener?: HTMLElement) => {
    if (records.current.has(owner) || !active() || !allowed || !online) return;
    records.current.set(owner, {
      kind: 'bulk',
      action: 'bulk_apply',
      scopeKind: 'category',
      direction: 'raise',
      percentageText: '',
      phase: 'preview',
      pageOffset: 0,
      note: '',
      submitted: false,
      accepted: false,
      uncertain: false,
      running: false,
      hidden: false,
      opener,
      openerLabel: opener?.getAttribute('aria-label') ?? opener?.textContent ?? undefined,
    });
    setCompletion(undefined);
    notify();
  };
  const openOperationUndo = (operationId: string, opener?: HTMLElement) => {
    if (records.current.has(owner) || !active() || !allowed || !online) return;
    const a: OperationUndoEntry = {
      kind: 'operation_undo',
      action: 'operation_undo',
      operationId,
      phase: 'previewing',
      pageOffset: 0,
      note: '',
      submitted: false,
      accepted: false,
      uncertain: false,
      running: false,
      hidden: false,
      opener,
      openerLabel: opener?.getAttribute('aria-label') ?? opener?.textContent ?? undefined,
      surface: historySurface(opener),
    };
    records.current.set(owner, a);
    setCompletion(undefined);
    notify();
    void loadPreview(a);
  };
  const editableBulk = () => {
    const a = records.current.get(owner);
    return a?.kind === 'bulk' &&
      !a.submitted &&
      !a.running &&
      a.phase === 'preview' &&
      !a.hidden &&
      active() &&
      allowed &&
      online
      ? a
      : undefined;
  };
  const resetBulkPreview = (a: BulkEntry) => {
    a.preview = undefined;
    a.intent = undefined;
    a.pageOffset = 0;
    a.error = undefined;
    a.membership = undefined;
    a.previousIds = undefined;
    notify();
  };
  const confirmBatch = () => {
    const a = records.current.get(owner);
    if (
      !a ||
      (a.kind !== 'bulk' && a.kind !== 'operation_undo') ||
      a.submitted ||
      a.running ||
      a.hidden ||
      a.phase !== 'preview' ||
      !active() ||
      !online ||
      !allowed ||
      !a.preview ||
      a.preview.summary.blockingCount > 0
    )
      return;
    if (a.kind === 'bulk') {
      a.expectedChangedCount = a.preview.summary.changedCount;
      if (!a.intent || !a.preview || !a.preview.summary.changedCount) return;
      const intent = {
        ...a.intent,
        selection: Object.freeze({ ...a.intent.selection }),
        reviewedItemIds: Object.freeze([
          ...a.preview.reviewedItemIds,
        ]) as unknown as string[],
        expectedRevision: a.preview.revision,
      };
      a.previousIds = intent.reviewedItemIds;
      a.input = Object.freeze({
        ...intent,
        ...keys.current.forIntent([owner, 'bulk_apply', intent]),
      });
    } else {
      a.expectedChangedCount = a.preview.summary.eligibleCount;
      if (!a.preview.summary.eligibleCount) return;
      const intent = {
        operationId: a.operationId,
        reviewedMemberIds: Object.freeze([
          ...a.preview.reviewedMemberIds,
        ]) as unknown as string[],
        eligibleMemberIds: Object.freeze([
          ...a.preview.eligibleMemberIds,
        ]) as unknown as string[],
        expectedRevision: a.preview.revision,
        note: a.note || null,
      };
      a.previousIds = intent.reviewedMemberIds;
      a.previousEligibleIds = intent.eligibleMemberIds;
      a.input = Object.freeze({
        ...intent,
        ...keys.current.forIntent([owner, 'operation_undo', intent]),
      });
    }
    a.submitted = true;
    notify();
    void send();
  };
  return {
    attempt,
    bulkAttempt,
    operationUndoAttempt,
    openBulk,
    openOperationUndo,
    batchLaunchersBlocked: false,
    retryBatchPreview: () => {
      const a = records.current.get(owner);
      if (
        a &&
        (a.kind === 'bulk' || a.kind === 'operation_undo') &&
        !a.submitted &&
        !a.hidden &&
        a.preview &&
        (a.phase === 'stale' || a.phase === 'preview_failed')
      )
        void loadPreview(a, a.requestedOffset ?? a.pageOffset, true);
    },
    confirmBatch,
    bulkMagnitudeValid: bulkAttempt
      ? percentageBps(bulkAttempt.percentageText) !== undefined
      : false,
    chooseBulkKind: (kind: 'category' | 'vendor') => {
      const a = editableBulk();
      if (a) {
        a.scopeKind = kind;
        a.selection = undefined;
        a.selectionLabel = undefined;
        resetBulkPreview(a);
      }
    },
    selectBulk: (selection: PricingGoalSelection, label?: string) => {
      const a = editableBulk();
      if (a && selection.kind !== 'item') {
        a.selection = selection;
        a.selectionLabel = label;
        resetBulkPreview(a);
      }
    },
    bulkDirection: (direction: 'raise' | 'lower') => {
      const a = editableBulk();
      if (a) {
        a.direction = direction;
        resetBulkPreview(a);
      }
    },
    bulkPercentage: (raw: string) => {
      const a = editableBulk();
      if (a) {
        a.percentageText = raw;
        resetBulkPreview(a);
      }
    },
    previewBulk: () => {
      const a = editableBulk();
      if (!a?.selection) return;
      const magnitudeBps = percentageBps(a.percentageText);
      if (magnitudeBps === undefined) return;
      a.intent = { selection: { ...a.selection }, direction: a.direction, magnitudeBps };
      void loadPreview(a);
    },
    batchPage: (offset: number) => {
      const a = records.current.get(owner);
      if (
        a &&
        (a.kind === 'bulk' || a.kind === 'operation_undo') &&
        !a.submitted &&
        a.phase === 'preview' &&
        a.preview &&
        !a.hidden
      )
        void loadPreview(a, offset, true);
    },
    goalAttempt,
    locked: !!entry?.submitted,
    hasDraft: !!entry,
    openGoal,
    openGoalUndo,
    confirmGoal,
    chooseGoalKind: (kind: 'category' | 'vendor') => {
      const a = records.current.get(owner);
      if (
        !a ||
        a.action !== 'goal_apply' ||
        a.submitted ||
        a.running ||
        !active() ||
        !allowed ||
        !online
      )
        return;
      a.scopeKind = kind;
      a.selection = undefined;
      a.selectionLabel = undefined;
      a.preview = undefined;
      a.membership = undefined;
      a.previousIds = undefined;
      a.phase = 'preview';
      notify();
    },
    selectGoal: (selection: PricingGoalSelection, label?: string) => {
      const a = records.current.get(owner);
      if (
        !a ||
        a.action !== 'goal_apply' ||
        a.submitted ||
        a.running ||
        !active() ||
        !allowed ||
        !online
      )
        return;
      a.selection = selection;
      a.selectionLabel = label;
      a.preview = undefined;
      a.membership = undefined;
      a.previousIds = undefined;
      void loadPreview(a);
    },
    privateReadRefused: (error: unknown) => {
      const a = records.current.get(owner);
      if (!a || a.kind === 'auto' || !active()) return;
      if (!a.submitted) a.phase = 'preview_failed';
      void withholdPrivateReads(error, generation.current);
    },
    goalPage: (offset: number) => {
      const a = records.current.get(owner);
      if (
        a?.action === 'goal_apply' &&
        !a.submitted &&
        a.phase === 'preview' &&
        a.preview &&
        !a.hidden
      )
        void loadPreview(a, offset, true);
    },
    online,
    completion: completion?.owner === owner && allowed ? completion : undefined,
    openUndo,
    beginKeep,
    confirmUndo,
    note: (note: string) => {
      const a = records.current.get(owner);
      if (a && !a.submitted && !a.running && active()) {
        a.note = note;
        notify();
      }
    },
    cancel: () => {
      const a = records.current.get(owner);
      if (a && !a.submitted && active()) finish(a, 'cancel');
    },
    discardRejectedGoal: () => {
      // Abandoning a proven refusal does not restore any hidden private projection.
      const a = records.current.get(owner);
      if (
        a &&
        a.kind !== 'auto' &&
        a.terminalRejected &&
        a.submitted &&
        !a.accepted &&
        !a.uncertain &&
        !a.running &&
        active() &&
        allowed
      )
        finish(a, 'cancel');
    },
    retry: () => void send(),
    refresh: () => void refresh(),
    reviewAgain: () => {
      const a = records.current.get(owner);
      if (a && !a.accepted && (a.phase === 'stale' || a.phase === 'preview_failed')) {
        if (
          (a.action === 'goal_apply' && !a.selection) ||
          (a.action === 'bulk_apply' && !a.submitted && !a.intent)
        ) {
          a.hidden = false;
          a.phase = 'preview';
          a.error = undefined;
          notify();
        } else void loadPreview(a);
      }
    },
    version,
  };
}
export type PricingReviewWrite = ReturnType<typeof usePricingReviewWrite>;
